<template>
    <div class="text-center">
        <loading-overlay :loading="(loading || loadingMasterPassword)"></loading-overlay>
        <v-dialog
            v-model="show"
            width="unset"
            max-width="700"
            tabindex="-1"
            role="dialog"
            content-class="rounded-xl ma-0"
            aria-labelledby="TransferModal"
            aria-hidden="true"
            transition="v-expand-y-transition"
        >
            <v-overlay
                absolute
                :value="showMatcherPaymentMethodDisclaimer"
                opacity="0.8"
                v-if="matcherPaymentMethod && !isPoster"
            >
                <v-card
                    rounded="xl"
                    max-width="375px"
                    light
                >
                    <v-card-text class="pa-6">
                        <div class="mt-lg-24" style="max-width: 400px">
                            <v-sheet
                                class="mx-auto mb-6 white rounded-circle"
                                elevation="0"
                                max-width="170"
                                height="52"
                            >
                                <v-img
                                    :src="matcherPaymentMethod.logo"
                                    contain
                                    class="align-self-start mx-auto"
                                    height="52"
                                />
                            </v-sheet>

                            <v-sheet
                                color="grey lighten-4 pa-4"
                                rounded="xl"
                            >
                                <p v-if="showWorldremitInformation" class="title mb-0 mx-auto">
                                    This is a WorldRemit transfer, you will be redirected to WorldRemit to make payment.
                                    Don't worry if you don't have a WorldRemit account, we will help you create one
                                </p>
                                <p v-else class="title mb-0 mx-auto">
                                    This is a {{ matcherPaymentMethod.name }} transfer.
                                    <template v-if="selectedOrder.type === 'hybrid'">
                                        You will be redirected to {{ matcherPaymentMethod.name }} to make payment.
                                    </template>
                                    <template v-else>
                                        You will be redirected to your bank to make payment with {{ matcherPaymentMethod.name }}.
                                    </template>
                                </p>
                            </v-sheet>

                            <ol class="my-6">
                                <li>Do not send money on behalf of anyone. You risk being a money mule for cyber fraud.</li>
                                <li>Do not send money for illegal activities. All transactions are recorded and records can be subpoenaed by law enforcement.</li>
                                <li>Only send money to people you personally know and have met. Be wary of online romance scams.</li>
                            </ol>

                            <!-- <v-checkbox
                                v-model="hideMatchInformationDialog"
                                label="Don’t show this again."
                                class="mt-0"
                            /> -->

                            <div class="text-center">
                                <v-btn
                                    color="primary"
                                    class="rounded-lg text-none"
                                    large
                                    @click="showMatcherPaymentMethodDisclaimer = false"
                                >
                                    I understand
                                </v-btn>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-overlay>
            <v-card
                transition="v-expand-y-transition"
                class="elevation-12 rounded-xl"
                :class="{ 'payment-card': !showMatcherPaymentMethodDisclaimer }"
                width="89vw"
                max-width="700px"
                :loading="loading && 'white'"
            >
                <v-toolbar dark flat color="white">
                    <template v-if="currentStep < 3 || $vuetify.breakpoint.smAndUp">
                        <v-btn color="black" icon @click="cancel" v-if="currentStep < 2">
                            <v-icon>mdi-arrow-left</v-icon>
                        </v-btn>
                        <v-toolbar-title class="pl-0 black--text">
                            {{ transferModalTitle }}
                        </v-toolbar-title>
                        <v-spacer />
                    </template>
                    <div
                        id="clock"
                        class="position-relative orange lighten-3 px-2 px-md-6 py-1 py-md-3 rounded-lg"
                        :class="[{ 'd-none': !timerHasStarted, 'd-flex': timerHasStarted }]"
                    >
                        <div class="progress red lighten-2"></div>
                        <div
                            class="black--text"
                            :class="[{ 'short-height text-center': $vuetify.breakpoint.smAndDown }]"
                        >
                            <span class="hours title"></span>
                            <small>hr</small>
                        </div>
                        <div
                            :class="[{ 'short-height text-center': $vuetify.breakpoint.smAndDown }]"
                            class="mx-2 mx-lg-5 black--text"
                        >
                            <span class="minutes title"></span>
                            <small>mins</small>
                        </div>
                        <div
                            :class="[{ 'short-height text-center': $vuetify.breakpoint.smAndDown }]"
                            class="black--text"
                        >
                            <span class="seconds title"></span>
                            <small>secs</small>
                        </div>
                    </div>
                    <v-spacer />
                    <div class="rounded-circle green lighten-5 px-2 py-3">
                        <v-toolbar-title class="green--text text--lighten-2 body-2 font-weight-bold">
                            {{ selectedOrder.poster_currency }}
                        </v-toolbar-title>
                    </div>
                </v-toolbar>

                <v-tabs
                    :vertical="$vuetify.breakpoint.mdAndUp"
                    :class="[{ 'primary': isLastStep }]"
                >
                    <transfer-steps-tabs
                        :currentStep="selectedOrder.type === '3po' ? thirdPartyDialogStep + 1 : currentStep"
                        :orderType="selectedOrder.type"
                        :isPartialMatchAllowed="!!selectedOrder.is_partial_match_allowed"
                    />
                    <v-tab-item v-if="selectedOrder.type === '3po'">
                        <ThirdPartyDialog
                            v-if="!showExistingAccount"
                            :order="selectedOrder"
                            :step.sync="thirdPartyDialogStep"
                            @loading="loading = true"
                            @loaded="loading = false"
                            @initiateManualPayment="initiate3poManualPayment"
                        />

                        <existing-account
                            v-if="showExistingAccount"
                            :order="{
                                ...selectedOrder,
                                ...acceptedOrder,
                            }"
                            :recipientInfo="acceptedOrder.recipient.recipientInfo"
                            :isUsedBefore="isUsedBefore"
                            @proceed="redirectTo3PO"
                        />
                    </v-tab-item>
                    <v-tab-item v-else>
                        <MatcherAmountDialog
                            v-if="showMatcherAmountDialog"
                            :order="selectedOrder"
                            @cancel="onMatcherAmountCancel"
                            @confirm="onMatcherAmountConfirm"
                        />
                        <paymentRequest
                            v-if="showPaymentRequest"
                            v-model="showPaymentRequest"
                            :show-payment-request="showPaymentRequest"
                            :selectedOrder="selectedOrder"
                            :paymentTypes="[ selectedOrder.poster_payment_method_type ]"
                            @cancel="cancelRequest"
                            @continue="requestReceived"
                        />
                        <paymentRecipient
                            v-if="showPaymentRecipient"
                            :showReceipientFields.sync="showReceipientFields"
                            v-model="showPaymentRecipient"
                            :show-payment-recipient="showPaymentRecipient"
                            :selectedOrder="selectedOrder"
                            :paymentTypes="[ selectedOrder.poster_payment_method_type ]"
                            @cancel="cancelRecipient"
                            @continue="recipientReceived"
                        />
                        <confirmPaymentRequest
                            v-if="showConfirmPaymentRequest"
                            v-model="showConfirmPaymentRequest"
                            :fee="1.00"
                            :selected-order="selectedOrder"
                            :requestor="requestor"
                            :recipient="recipient"
                            :user-recipient="selectedUserRecipient"
                            :delivery-method="deliveryMethod"
                            :benefactor-email="benefactorEmail"
                            :payment-request-purpose="paymentRequestPurpose"
                            :payment-method="selectedOrder.poster_payment_method_type"
                            :note="note"
                            :show-confirm-payment-request="showConfirmPaymentRequest"
                            :coupon-prop="coupon"
                            :amount="requestAmount || selectedOrder.matcher_amount"
                            :posterAmount="requestPosterAmount || selectedOrder.poster_amount"
                            @accept="acceptPaymentRequest"
                            @cancel="cancelConfirmPaymentRequest"
                        />
                        <fee-wheel
                            v-if="showFeeModal"
                            @close="closeFeeModal"
                            :orderId="selectedOrder.id"
                            :amount="selectedOrder.type === 'p2p' ? selectedOrder.matcher_amount : selectedOrder.pb_amount"
                            :showWarning="selectedOrder.type === 'p2p' && selectedOrder.matcher_country === 'US' && selectedOrder.matcher_amount >= 450"
                            :currencyCode="selectedOrder.matcher_currency"
                            :pbToMatcherExchangeRate="selectedOrder.pb_to_matcher_exchange_rate"
                            @received="feeReceived"
                        />
                        <confirm-transfer
                            v-if="showConfirmTransfer"
                            v-model="showConfirmTransfer"
                            :fee="fee"
                            :selected-order="selectedOrder"
                            :recipient="recipient"
                            :user-recipient="selectedUserRecipient"
                            :payment-type="paymentType"
                            :show-confirm-transfer="showConfirmTransfer"
                            :coupon-prop="coupon"
                            @accept="acceptTransfer"
                            @cancel="cancelConfirmTransfer"
                        />
                        <make-payment
                            v-if="showMakePayment"
                            ref="makePayment"
                            :acceptedOrder="acceptedOrder"
                            :couponProp="coupon"
                            :skip-payment-method-check="!!skipPaymentMethodCheck"
                            :isFromTransfers="isFromTransfers"
                            @pay="pay"
                            @close="closeMakePaymentModal"
                            @startTimer="initCountdownTimer"
                        />
                        <pisErrorDialog
                            v-if="showPISErrorDialog && postersBeneficiaryInfo"
                            :recipientInfo="postersBeneficiaryInfo"
                        />
                        <select-your-bank
                            v-if="showSelectYourBank"
                            v-model="showSelectYourBank"
                            :postersBeneficiaryInfo="postersBeneficiaryInfo"
                            :acceptedOrder="acceptedOrder"
                            @bankSelected="onBankSelected"
                            @close="closeSelectYourBankModal"
                        />
                        <existing-account
                            v-if="showExistingAccount"
                            :order="{
                                ...selectedOrder,
                                ...acceptedOrder,
                                company: selectedOrder.company || postersBeneficiaryInfo.company,
                            }"
                            :recipientInfo="acceptedOrder.type === 'hybrid' ? postersBeneficiaryInfo : selectedUserRecipient.recipientInfo"
                            :isUsedBefore="isUsedBefore"
                            @proceed="redirectTo3PO"
                        />
                    </v-tab-item>
                </v-tabs>
            </v-card>
        </v-dialog>

        <WorldremitAccountCreationDialog
          v-if="selectedOrder.type === 'hybrid'"
          ref="WorldremitAccountCreationDialog"
        />

        <WorldremitLoginDialog
          v-if="selectedOrder.type === 'hybrid'"
          ref="WorldremitLoginDialog"
        />

        <WorldremitMakePaymentDialog
          v-if="selectedOrder.type === 'hybrid'"
          ref="WorldremitMakePaymentDialog"
          @success="redirectToTransfers"
        />

        <haveAccountWithCompany
            ref="haveAccountWithCompany"
        />
    </div>
</template>

<script>
/* eslint-disable camelcase */
/* eslint-disable arrow-body-style */
    import ServerUtils from '../../../services/ServerUtils';
    import paymentRecipient from './payment-recipient';
    import paymentRequest from './payment-request';
    import makePayment from './make-payment';
    import feeWheel from './fee-wheel';
    import confirmTransfer from './confirm-transfer';
    import confirmPaymentRequest from './confirm-payment-request';
    import selectYourBank from './select-your-bank';
    import TransferStepsTabs from './transfer-steps-tabs.vue';
    import ExistingAccount from './existing-account.vue';
    import pisErrorDialog from './pis-error-dialog.vue';
    import WorldremitAccountCreationDialog from '../../components/worldremit/widget.vue';
    import WorldremitLoginDialog from '../../components/worldremit/LoginDialog.vue'
    import WorldremitMakePaymentDialog from '../../components/worldremit/MakePayment.vue'
    import MatcherAmountDialog from './MatcherAmountDialog.vue';

    import ThirdPartyDialog from './ThirdPartyDialog.vue';

    import haveAccountWithCompany from './haveAccountWithCompany.vue';

    import formatCurrency from '../../../modules/formatCurrency';
    import formatShortCurrency from '../../../modules/formatShortCurrency';
    import formatExchangeRate from '../../../modules/formatExchangeRate';

    import FormFillerService from "../../../services/FormFillerService";
    import KlarnaService from "../../../services/KlarnaService";
    import PlaidService from '../../../services/PlaidService';

    import { mapGetters } from 'vuex';

    export default {
        name: "transfer-modal",
        data,
        mounted: mounted,
        props: {
            isPoster: {
                type: Boolean,
                default: false,
            },
            selectedOrder: Object,
            requestor: Object,
            showTransferModal: Boolean,
            value: Boolean,
            pendingMatch: {},
            paymentTypes: Array,
            isFromTransfers: Boolean,
        },
        computed: getComputed(),
        filters: {
            currency(amount) {
                return formatCurrency(amount);
            },
            shortCurrency(amount) {
                return formatShortCurrency(amount);
            },
            exchangeRate(amount) {
                return formatExchangeRate(amount);
            },
        },
        methods: getMethods(),
        components: {
            paymentRecipient,
            paymentRequest,
            makePayment,
            feeWheel,
            confirmTransfer,
            confirmPaymentRequest,
            selectYourBank,
            TransferStepsTabs,
            ExistingAccount,
            WorldremitAccountCreationDialog,
            WorldremitLoginDialog,
            WorldremitMakePaymentDialog,
            pisErrorDialog,
            haveAccountWithCompany,
            MatcherAmountDialog,
            ThirdPartyDialog,
        },
    };

    function data() {
        return {
            loading: false,
            loadingMasterPassword: false,
            fee: 1.00,
            pageTitle: 'Transfer Modal',
            showMatcherAmountDialog: false,
            showPaymentRecipient: false,
            showPaymentRequest: false,
            showMakePayment: false,
            showExistingAccount: false,
            showConfirmTransfer: false,
            showFeeModal: false,
            showConfirmPaymentRequest: false,
            showSelectYourBank: false,
            recipient: {},
            selectedUserRecipient: {},
            matcherPaymentPurpose: null,
            paymentType: '',
            acceptedOrder: {},
            deliveryMethod: '',
            benefactorEmail: '',
            paymentRequestPurpose: '',
            requestorDeliveryMethod: null,
            requestAmount: null,
            requestPosterAmount: null,
            note: '',
            coupon: {},
            isPrevTransfer: false,
            postersBeneficiaryInfo: null,
            kaoshiCountries: [],
            showReceipientFields: false,
            timerHasStarted: false,
            showMatcherPaymentMethodDisclaimer: true,
            showPISErrorDialog: false,
            isUsedBefore: true,
            thirdPartyDialogStep: 0,
        };
    }

    async function mounted() {
        const vm = this;
        this.showMatcherPaymentMethodDisclaimer = !this.hideMatchInformationDialog;
        fromTransfersCheck();
        toShowPaymentRequest();
        this.loadPrevTransfer();

        this.loadCountries();

        function fromTransfersCheck() {
            if (vm.isFromTransfers) {
                vm.beginPayment();
            } else {
                 if (!vm.isPoster) {
                    if (vm.selectedOrder.is_partial_match_allowed) {
                        vm.showMatcherAmountDialog = true;
                    } else {
                        vm.showPaymentRecipient = true;
                    }
                 } else {
                     vm.showPaymentRecipient = false;
                 }
            }
        }

        function toShowPaymentRequest() {
            if (vm.isPoster) {
                vm.loading = true;

                Vue.backendApi().user(vm.$store.state.user.id).getDeliveryMethods().then(success, error);
                // eslint-disable-next-line no-inner-declarations
                function success(response) {
                    vm.loading = false;
                    const deliveryMethods = response.data;

                    // if have any registered delivery methods
                    if (!deliveryMethods || !deliveryMethods.length){
                        vm.showPaymentRequest = false;
                        vm.$swal({
                            type: 'error',
                            title: 'No delivery methods found',
                            text: 'You have no registered delivery method yet. Please register one to complete your request successfully.',
                        }).then((result) => {
                            if (result.value) {
                                vm.showTutorialVideo('delivery');
                            }
                        });
                    } else {
                        const orderDeliveryMethod = vm.getDeliveryMethod(vm.selectedOrder.matcher_payment_method_type);
                        let hasCompatibleDeliveryMethods = false;

                        if (vm.selectedOrder.type === '3po') {
                            if (orderDeliveryMethod.key === 'pickup') {
                                // Allow transaction if order has pickup delivery method and name is verivied
                                if (vm.$_user.name_verified_at) {
                                    hasCompatibleDeliveryMethods = true;
                                }
                            } else {
                                // Allow transaction if user has compatible delivery methods
                                const filteredDeliveryMehtods = deliveryMethods.filter(({ type }) => {
                                    return type.method === orderDeliveryMethod.key;
                                });
                                hasCompatibleDeliveryMethods = filteredDeliveryMehtods.length > 0;
                            }
                        } else {
                            hasCompatibleDeliveryMethods = true;
                        }

                        // if have registered delivery methods but is incompatible with the selected order deliveru method
                        if (!hasCompatibleDeliveryMethods) {
                            vm.showPaymentRequest = false;
                            vm.$swal({
                                type: 'error',
                                title: 'Incompatible delivery method',
                                text: 'Your registered delivery method is incompatible with the remittance service you selected. Choose a remittance offer who delivery method matches your registered delivery method.',
                                preConfirm: () => {
                                    vm.$emit('cancel');
                                },
                            });
                        } else {
                            vm.showPaymentRequest = true;
                        }
                    }
                }
                function error(err) {
                    vm.loading = false;

                    vm.$swal({
                        type: 'error',
                        title: 'Error retrieving delivery methods',
                        text: err,
                        preConfirm: () => {
                            vm.$emit('cancel');
                        },
                    });
                }
            }else{
                vm.showPaymentRequest = false;
            }
        }
    }

    function getMethods() {
        return {
            cancelRecipient,
            cancelRequest,
            cancelConfirmTransfer,
            cancelConfirmPaymentRequest,
            recipientReceived,
            requestReceived,
            acceptMatchedOffer,
            validatePaymentRequest,
            acceptTransfer,
            acceptPaymentRequest,
            pay,
            beginPayment,
            initiateKlarnaPayment,
            completeKlarnaPayment,
            initiatePlaidPayment,
            completePlaidPayment,
            initiateDefaultPayment,
            initiateHybridOrderPayment,
            initiateUnifiedPayment,
            onBankSelected,
            getDeliveryMethod,
            feeReceived,
            loadPrevTransfer,
            redirectToTransfers,
            closeFeeModal,
            closeMakePaymentModal,
            closeSelectYourBankModal,
            loadCountries,
            cancel,
            initCountdownTimer,
            initiate3POTransaction,
            redirectTo3PO,
            capitalize(value) {
                return value.charAt(0).toUpperCase() + value.slice(1);
            },
            showPISError,
            askIfHaveAccountWithCompany,
            onMatcherAmountCancel,
            onMatcherAmountConfirm,
            async initiate3poManualPayment (thirdPartyOrder) {
                this.acceptedOrder = thirdPartyOrder;
                const { company } = thirdPartyOrder

                if (company) {
                    await this.askIfHaveAccountWithCompany(company);
                }

                this.showExistingAccount = true;
            },
        };

        async function redirectTo3PO(data) {
            if(this.selectedOrder.type === '3po') {
                this.initiate3POTransaction(data);
                return;
            }

            if(this.selectedOrder.type === 'hybrid') { // hybrid
                this.initiateHybridOrderPayment(data);
            }
        }

        function initiate3POTransaction({ url }) {
            const vm = this;
            FormFillerService.dispatchPaymentEvent(vm.acceptedOrder.id, '3po', url);
            vm.popupCenter(url);

            // We need to give the window time to open before we can leave this page
            setTimeout(() => {
                vm.$router.push('/transfers');
            }, 1000);
        }

        function initCountdownTimer(timeLeft) {
            const vm = this;

            const endTime = new Date(Date.parse(new Date()) + timeLeft * 1000);
            const clock = document.getElementById('clock');
            const hoursDiv = clock.querySelector('.hours');
            const minutesDiv = clock.querySelector('.minutes');
            const secondsDiv = clock.querySelector('.seconds');
            const progressDiv = clock.querySelector('.progress');

            function getTimeRemaining(endTime) {
                const total = Date.parse(endTime) - Date.parse(new Date());
                const seconds = Math.floor((total / 1000) % 60);
                const minutes = Math.floor((total / 1000 / 60) % 60);
                const hours = Math.floor((total / (1000 * 60 * 60)) % 24);

                return {
                    total,
                    hours,
                    minutes,
                    seconds
                };
            }

            function updateClock() {
                const time = getTimeRemaining(endTime);

                hoursDiv.innerHTML = ('0' + time.hours).slice(-2);
                minutesDiv.innerHTML = ('0' + time.minutes).slice(-2);
                secondsDiv.innerHTML = ('0' + time.seconds).slice(-2);

                const progressWidth = Number(progressDiv.style.width.slice(0,-1)) || 100;
                progressDiv.style.width = `${progressWidth - (100/((time.total/1000)-1))}%`;

                if (time.total <= 0) {
                    clearInterval(timeInterval);
                    vm.timerHasStarted = false;
                }
            }

            updateClock();
            const timeInterval = setInterval(updateClock, 1000);
            vm.timerHasStarted = true;
        }

        function cancel() {
            if(this.showReceipientFields && this.showPaymentRecipient) {
                this.showReceipientFields = false;
            }
            else if(this.showPaymentRequest) {
                this.cancelRequest();
            }
            else if(this.showPaymentRecipient) {
                this.cancelRecipient();
            }
            else if(this.showFeeModal) {
                this.showFeeModal = false;

                if(this.isPoster) {
                    this.showPaymentRequest = true;
                    return;
                }

                this.showReceipientFields = false;
                this.showPaymentRecipient = true;
            }
        }

        function getDeliveryMethod(val) {
            const deliveryMethod = this.storeDeliveryMethods.find(({ methods }) => methods.includes(val))
            return deliveryMethod || {}
        }

        function requestReceived(data) {
            this.recipient = data.recipientInfo;
            this.benefactorEmail = data.benefactorEmail;
            this.deliveryMethod = data.deliveryMethod;
            this.paymentRequestPurpose = data.paymentRequestPurpose;
            this.note = data.note;
            this.requestorDeliveryMethod = data.requestorDeliveryMethod;
            this.requestAmount = data.amount;
            this.requestPosterAmount = data.poster_amount;

            this.showConfirmPaymentRequest = true;
            this.showPaymentRequest = false;
        }

        async function recipientReceived(data = { paymentType: null, recipientId: null, recipientInfo: {}, matcherPaymentPurpose: null }) {
            const vm = this;

            const {
                paymentType,
                recipientId,
                recipientInfo: recipient,
                matcherPaymentPurpose,
            } = data;

            vm.selectedUserRecipient = data;
            vm.recipient = recipient;
            vm.paymentType = paymentType;
            vm.recipientId = recipientId;
            vm.matcherPaymentPurpose = matcherPaymentPurpose;
            vm.showPaymentRecipient = false;

            const {
              id: orderId,
              type,
              matcher_country: matcherCountry,
              matcher_amount: matcherAmount,
              matcher_payment_provider: matcherPaymentProvider,
            } = vm.selectedOrder;

            if(type === 'p2p' || type === 'hybrid') {
                try {
                    this.loading = true;

                    const { data: feeOptions } = await Vue.backendApi().order(orderId)
                        .getMatcherFeeOptions({
                            type,
                            matcherPaymentProvider
                        });


                    this.loading = false;

                    if (feeOptions.useRandom) {
                        vm.showFeeModal = true;
                        return true;
                    } else {
                        const fee = (matcherAmount * feeOptions.percentage) / 100;

                        Vue.set(this, 'fee', fee);
                        Vue.set(this.selectedOrder, 'matcher_fee_percentage', feeOptions.percentage);

                        this.showConfirmTransfer = true;
                        return true;
                    }
                } catch (err) {
                    this.loading = false;
                    this.$swal({
                        type: 'error',
                        title: 'Error loading fee options',
                        text: err,
                    });
                    throw err;
                }
            }

            Vue.set(this, 'fee', 1.00);
            this.showConfirmTransfer = true;
        }

        function feeReceived(fee) {
            Vue.set(this, 'fee', fee.fee);
            Vue.set(this.selectedOrder, 'matcher_fee_id', fee.id);
            Vue.set(this.selectedOrder, 'matcher_fee_percentage', fee.percentage);
            this.showConfirmTransfer = true;
            this.showFeeModal = false;
        }

        function closeFeeModal() {
            const vm = this;
            vm.showFeeModal = false;
            vm.$emit('cancel');
        }

        function closeMakePaymentModal() {
            const vm = this;
            vm.showMakePayment = false;
            vm.$emit('cancel');
        }

        function closeSelectYourBankModal() {
            const vm = this;
            console.log('selected')
            vm.showSelectYourBank = false;
            vm.$emit('cancel');
        }

        function cancelRecipient() {
            const vm = this;
            vm.showConfirmTransfer = false;
            vm.$emit('cancel');
        }

        function cancelRequest() {
            const vm = this;
            vm.showConfirmTransfer = false;
            vm.$emit('cancel');
        }

        function cancelConfirmTransfer() {
            const vm = this;
            vm.cancelConfirmTransfer = false;
            vm.$emit('cancel');
        }

        function cancelConfirmPaymentRequest() {
            const vm = this;
            vm.cancelConfirmPaymentRequest = false;
            vm.$emit('cancel');
        }

        function acceptPaymentRequest() {
            const vm = this;

            // store request

            let fee = vm.selectedOrder.poster_fee || vm.selectedOrder.fakeFee;

            if (vm.selectedOrder.is_partial_match_allowed && vm.requestPosterAmount) {
                fee = Math.round(100 * fee * (vm.requestPosterAmount / vm.selectedOrder.poster_amount)) / 100;
            }

            const order_id = (vm.selectedOrder.type === 'p2p' || vm.selectedOrder.type === 'hybrid') ? vm.selectedOrder.id : null;

            // Get translated Payment method to store in 3po order
            const payment_method = vm.paymentMethods.find((item) => {
                const method = item.methods.find((methodItem) => methodItem === vm.selectedOrder.poster_payment_method_type);
                return !!method;
            });

            vm.validatePaymentRequest({
                benefactorEmail: vm.benefactorEmail,
                paymentRequestPurpose: vm.paymentRequestPurpose,
                note: vm.note,
                payment_method: payment_method.name,
                delivery_method: vm.deliveryMethod,
                amount: vm.requestAmount || vm.selectedOrder.matcher_amount,
                poster_amount: vm.requestPosterAmount || vm.selectedOrder.poster_amount,
                fee,
                exchange_rate: vm.selectedOrder.exchange_rate,
                type: vm.selectedOrder.type,
                requestor_currency: vm.selectedOrder.poster_currency,
                requestor_country: vm.selectedOrder.poster_country,
                benefactor_currency: vm.selectedOrder.matcher_currency,
                benefactor_country: vm.selectedOrder.matcher_country,
                order_id,
                company_slug: vm.selectedOrder.company_slug,
                requestor_delivery_method_id: vm.requestorDeliveryMethod.id,
            });
        }

        async function validatePaymentRequest(args = {
                benefactorEmail: null,
                paymentRequestPurpose: null,
                note: null,
                payment_method: null,
                delivery_method: null,
                amount: null,
                poster_amount: null,
                fee: null,
                exchange_rate: null,
                type: null,
                requestor_currency: null,
                benefactor_currency: null,
                requestor_delivery_method_id: null,
            }) {

            const vm = this;
            vm.loading = true;

            const {
                benefactorEmail,
                paymentRequestPurpose,
                note,
                payment_method,
                delivery_method,
                amount, fee, poster_amount,
                exchange_rate,
                type,
                requestor_currency,
                requestor_country,
                benefactor_currency,
                benefactor_country,
                order_id,
                company_slug,
                requestor_delivery_method_id,
            } = args;

            let payload = {
                benefactor_email: benefactorEmail,
                purpose: paymentRequestPurpose,
                note: note,
                payment_method: payment_method,
                delivery_method: delivery_method,
                amount,
                poster_amount,
                fee: fee,
                exchange_rate: exchange_rate,
                type,
                requestor_currency,
                requestor_country,
                benefactor_currency,
                benefactor_country,
                order_id,
                company_slug,
                requestor_delivery_method_id,
            };

            if (payload.type === 'hybrid') {
                payload.matcher_payment_provider = this.selectedOrder.matcher_payment_provider;
                payload.matcher_payment_method_type = this.selectedOrder.poster_payment_method_type; // TODO: fix this, we use worng values in filter on the remittance page
                payload.matcher_payment_method_fee = this.selectedOrder.matcher_payment_method_fee;
                payload.pb_to_matcher_exchange_rate = this.selectedOrder.pb_to_matcher_exchange_rate;
            }

            Vue.backendApi().paymentRequest().storePaymentRequest(payload).then(success, error);

            function success(response) {
                vm.loading = false;
                vm.$swal({
                    type: 'success',
                    title: 'Success payment request',
                    preConfirm: () => {
                        vm.$router.push({ name: 'requests' });
                    },
                })
                vm.showPaymentRequest = false;
                vm.showConfirmPaymentRequest = false;
                vm.show = false;
            }

            function error(error) {
                vm.loading = false;
                vm.$swal({
                    type: 'error',
                    title: 'Oops, I Am So Sorry!',
                    text: error
                })
                vm.showPaymentRequest = false;
                vm.showConfirmPaymentRequest = false;
                vm.show = false;
            }
        }

        function acceptTransfer() {
            const vm = this;

            vm.acceptMatchedOffer({ // TODO: name is wrong, it actually matches order
                paymentType: vm.paymentType,
                userRecipient: vm.selectedUserRecipient,
                matcherPaymentPurpose: vm.matcherPaymentPurpose,
            });
        }

        async function acceptMatchedOffer(args = {
                paymentType: null,
                recipientId: null,
                recipientInfo: {},
                matcherPaymentPurpose: null
            })
        {

            const vm = this;
            vm.loading = true;

            const { matcher_payment_provider: service } = this.selectedOrder;
            const { paymentType, recipientId, recipientInfo, matcherPaymentPurpose } = args;

            /*
             * We create the third party order here.
             *
             * Currently, third party orders are defined by their being '.fake'.
             *
             * @todo MUCH LATER - make an order/offer service - this is not the right place to be making orders.
             */
            if (vm.selectedOrder.fake) {
                // moved to ThridPartyDialog.vue
            } else {
                let matchPayload = {
                    type: this.selectedOrder.type,
                    matcher_payment_purpose: matcherPaymentPurpose,
                    recipient_id: vm.recipientId,
                    matcher_fee_id: vm.selectedOrder.matcher_fee_id,
                    matcher_fee_percentage: vm.selectedOrder.matcher_fee_percentage,
                    poster_amount: vm.selectedOrder.poster_amount,
                }

                if (this.selectedOrder.type === 'hybrid') {
                    matchPayload.matcher_payment_provider = this.selectedOrder.matcher_payment_provider;
                    matchPayload.matcher_payment_method_type = this.selectedOrder.poster_payment_method_type; // TODO: fix this, we use worng values in filter on the remittance page
                    matchPayload.matcher_payment_method_fee = this.selectedOrder.matcher_payment_method_fee;
                    matchPayload.matcher_currency = this.selectedOrder.matcher_currency;
                    matchPayload.pb_to_matcher_exchange_rate = this.selectedOrder.pb_to_matcher_exchange_rate;
                }

                Vue.backendApi().order(this.selectedOrder.id)
                    .matchOffer(matchPayload).then(success, error);

                async function success(response) {
                    Vue.set(vm, 'acceptedOrder', response.data);
                    vm.loading = false;

                    const { matcher_country: matcherCountry } = response.data;
                    if (matcherCountry === 'GH' || matcherCountry === 'KE' || matcherCountry === 'ZA') {
                        vm.redirectToTransfers();
                        return;
                    }

                    vm.showMakePayment = true;
                    vm.showConfirmTransfer = false;
                }

                function error(error) {
                    vm.loading = false;
                    vm.$swal({
                        type: 'error',
                        title: 'Error matching offer',
                        text: error
                    })
                }
            }
        }

        function showPISError() {
          this.showPISErrorDialog = true,
          this.showMakePayment = false;
        }

        async function askIfHaveAccountWithCompany(company) {
            if (!company) {
                return;
            }

            if (!company.name) {
                return;
            }

            let isUsedBefore = false

            this.loading = true;
            try {
                const { data } = await Vue.backendApi().user().hasAccountWith(company.name);
                isUsedBefore = data.isUsedBefore
            } catch (err) {
                // do nothing
            } finally {
                this.loading = false;
            }

            if (!isUsedBefore) {
                isUsedBefore = await this.$refs.haveAccountWithCompany.confirm(company);
            }

            this.isUsedBefore = isUsedBefore;
        }

        function onMatcherAmountCancel () {
            this.showMatcherAmountDialog = false;
            this.$emit('cancel');
        }

        function onMatcherAmountConfirm (amount) {
            let multiplier = amount / this.selectedOrder.poster_amount;
            if (multiplier > 1) {
                multiplier = 1;
            }

            const {
                type,
                matcher_amount: matcherAmount,
            } = this.selectedOrder;

            const pbAmount = type === 'hybrid' ? this.selectedOrder.pb_amount : matcherAmount;

            let newMatcherAmount = matcherAmount * multiplier;
            if (newMatcherAmount > matcherAmount) {
                newMatcherAmount = matcherAmount;
            }

            let newPbAmount = pbAmount * multiplier;
            if (newPbAmount > pbAmount) {
                newPbAmount = pbAmount;
            }

            Vue.set(this.selectedOrder, 'matcher_amount', newMatcherAmount);
            Vue.set(this.selectedOrder, 'pb_amount', newPbAmount);
            Vue.set(this.selectedOrder, 'poster_amount', amount);

            this.showMatcherAmountDialog = false;
            this.showPaymentRecipient = true;
        }
        /**
          * @param {object} postersBeneficiary
          * {
          *     provider: enum<'PLAID', 'KLARA'>,
          *     method:  enum<'ZELLE', 'INTERAC', 'SEPA', 'FASTER'>,
          *     iban: ?string,            SEPA
          *     bic: ?string,             SEPA
          *     acocunt: ?string,         FASTER
          *     sort_code: ?string,       FASTER
          *     zelle_email: ?string,     ZELLE
          *     interac_email: ?string,   INTERAC
          *     name: string,
          *         ...
          * }
          */
        async function pay(postersBeneficiaryInfo) {
            if (postersBeneficiaryInfo.paymentUrl) {
                return this.popupCenter(postersBeneficiaryInfo.paymentUrl)
            }

            this.postersBeneficiaryInfo = postersBeneficiaryInfo;

            if (postersBeneficiaryInfo.useUnifiedApi) {
                return this.initiateUnifiedPayment();
            }

            if (postersBeneficiaryInfo.company) {
                await this.askIfHaveAccountWithCompany(postersBeneficiaryInfo.company);
            }

            const {
                provider,
                method,
                isNgHybrid=false
            } = postersBeneficiaryInfo;
            const orderId = this.acceptedOrder.order_id;

            if (method === 'SEPA' || method === 'FASTER' ) { // or another PIS
                this.loading = true;
                // Klarna PIS requires back end to initiate payment
                // so need to call an API
                Vue.backendApi().order(orderId).initiatePayment()
                    .then((response) => {
                        this.loading = false;
                        const { data } = response;
                        switch (data.provider) {
                            case 'KLARNA':
                                if (data.method === 'FASTER') {
                                    return this.initiatePlaidPayment(orderId, data); // temporary
                                } else {
                                    return this.initiateKlarnaPayment(orderId, data);
                                }
                                break;
                            case 'PLAID':
                                if (data.method === 'FASTER' || data.method === 'SEPA') {
                                    this.initiatePlaidPayment(orderId, data);
                                } else {
                                    this.initiateDefaultPayment();
                                }
                                break;
                            default:
                                this.initiateDefaultPayment();
                                break;
                        }
                    })
                    .catch((err) => {
                        this.loading = false;
                        this.showPISError();
                    });
            } else if (provider === 'MONO') {
                this.showMakePayment = false;
                this.showExistingAccount = !isNgHybrid;
            } else { // ZELLE, INTERAC
              this.initiateDefaultPayment();
            }
        }

        async function initiateUnifiedPayment () {
            const { id: matchId, type } = this.acceptedOrder
            if (type !== 'hybrid') return

            const { matcher_payment_provider: service } = this.selectedOrder;

            try {
                this.loading = true;
                const { data: config } = await Vue.backendApi()
                    .unified()
                    .getPaymentPayload(matchId);

                await this.waitForUnifiedPay()

                window.UnifiedPay.unmount()
                window.UnifiedPay.mount(null, {
                    ...config,
                    skipMerchantScreen: true,
                    service,
                    token: process.env.MIX_UNIFIED_API_TOKEN,
                    onTransactionSuccess: () => {
                        this.showMakePayment = false;
                        this.redirectToTransfers();
                    },
                    onError: async (type, error) => {
                        console.log('onError', type, error)
                        const { response } = error;

                        if (response && response.status === 500) {
                            this.$nextTick(() => {
                                window.UnifiedPay.unmount();
                            });

                            const { value: payManually } = await this.$swal({
                                type: 'error',
                                title: `Error initiating payment.
                                    Please try again or proceed manually.`,
                                showCancelButton: true,
                                showConfirmButton: true,
                                confirmButtonText: 'Pay manually',
                                cancelButtonText: 'Close and Retry',
                            });

                            if (payManually) {
                                this.$refs.makePayment.pay(false);
                            } else {
                                this.initiateUnifiedPayment()
                            }
                        }
                    },
                    onOauthSuccess: async (credentials) => {
                        try {
                            await Vue.backendApi().unified().storeOauthTokens(credentials)
                        } catch (err) {
                            console.error(err)
                        }
                    }
                });

                window.UnifiedPay.open()
            } catch (err) {
                this.$swal({
                    type: 'error',
                    title: 'Error initiating payment',
                    text: err,
                });
            } finally {
                this.loading = false;
            }
        }

        function initiateKlarnaPayment(orderId, data) {
            const vm = this;
            const flowId = data.flow_id;
            const sessionId = data.session_id;
            const clientToken = data.client_token;
            const klarnaService = new KlarnaService({
                autoClose: true,
                hideTransitionOnFlowEnd: true,
                onLoad: () => {
                    this.loading = false;
                },
                onError: (error) => {
                    this.showPISError();
                    this.redirectToTransfers();
                },
            });

            vm.loading = true;
            return klarnaService.init()
                .then(() => {
                    klarnaService.startFlow(clientToken, {
                        onFinished() {
                          vm.completeKlarnaPayment(orderId, sessionId, flowId);
                        },
                    });
                })
                .catch((error) => {
                    vm.loading = false;
                    this.showPISError();
                });
        }

        function completeKlarnaPayment(orderId, sessionId, flowId) {
          const vm = this;
          vm.loading = true;
          Vue.backendApi().order(orderId)
              .completePayment('KLARNA', {
                  sessionId,
                  flowId
              })
              .then((response) => {
                  vm.loading = false;
                  const { state } = response.data;
                  if(state && state.toUpperCase() === 'FINISHED') {
                    vm.showMakePayment = false;
                    vm.show = false;
                    vm.$swal({
                        type: 'success',
                        title: 'Payment completed successfully',
                        text: '',
                    });
                  } else {
                    vm.$swal({
                        type: 'error',
                        title: 'Transfer error',
                        text: `Transfer status is ${state}. Please try again`,
                    });
                  }

                  vm.redirectToTransfers();
              })
              .catch((error) => {
                  vm.loading = false;
                  vm.$swal({
                      type: 'error',
                      title: 'Error checking payment status',
                      text: error.message,
                  });

                  vm.redirectToTransfers();
              });
        }

        async function initiatePlaidPayment(orderId, data) {

          const onSuccess = () => {
            this.completePlaidPayment(orderId, data.payment_id);
          };

          const onExit = (err, metadata) => {
              console.log('Plaid onExit', err, metadata);
              this.loading = false;
              if (err) {
                  this.showPISError();
              }
              this.redirectToTransfers();
          };

          const plaidConfig = {
            onSuccess: onSuccess,
            onExit: onExit,
            token: data.tokenResponse.link_token,
          };

          localStorage.setItem('KOSHI_PLAID_PIS_LINK_TOKEN', plaidConfig.token);
          localStorage.setItem('KOSHI_PLAID_PIS_ORDER_ID', orderId);
          localStorage.setItem('KOSHI_PLAID_PIS_PAYMENT_ID', data.payment_id);

          const plaidService = new PlaidService();
          this.loading = true;

          try {
              await plaidService.init();
              plaidService.create(plaidConfig);
              await plaidService.open();
              this.loading = false;
          } catch (err) {
              this.loading = false;
              this.showPISError();
          }
        }


        function completePlaidPayment(orderId, paymentId) {
          const vm = this;
          vm.loading = true;

          Vue.backendApi().order(orderId)
              .completePayment('PLAID', {
                  paymentId,
              })
              .then((response) => {
                  vm.loading = false;
                  const { state } = response.data;
                  vm.showMakePayment = false;
                  vm.show = false;
                  vm.$swal({
                      type: 'success',
                      title: 'Payment completed successfully',
                      text: '',
                  });

                  vm.redirectToTransfers();
              })
              .catch((error) => {
                  vm.loading = false;
                  vm.$swal({
                      type: 'error',
                      title: 'Error checking payment status',
                      text: error,
                  });

                  vm.redirectToTransfers();
              });
        }

        function initiateDefaultPayment() {
            const vm = this;
            vm.showSelectYourBank = true;
            vm.showMakePayment = false;
        }

        async function initiateHybridOrderPayment({ url }) {
            const orderId = this.acceptedOrder.order_id;
            const {
                phone,
            } = this.postersBeneficiaryInfo;

            FormFillerService.dispatchPaymentEvent(orderId, 'hybrid', url, {
                phone,
            });

            this.popupCenter(url, '');
            this.loading = false;

            this.redirectToTransfers();
        }
        /**
         * When transfer modal is triggered from the transfers page (transfers.vue)
         * it skips the select recipient and confirm-transfer modals (since they have
         * presumably already been filled out).
         *
         * Order.id from
         * Vue.backendApi().order().listUsersMatchedOrders(vm.$store.state.user.id) is
         * oddly different from order.id from Vue.backendApi().order().globalOffers()
         */

        async function beginPayment() {
            const vm = this;
            // make-payment.vue expects "acceptedOrder"
            // TODO: remove this: match = order
            vm.acceptedOrder = vm.selectedOrder;
            // make sure no other data pops up in the modal for any reason
            vm.showPaymentRecipient = false;
            vm.showConfirmTransfer = false;

            vm.showMakePayment = true;
        }

        function onBankSelected(url) {
            FormFillerService.dispatchPaymentEvent(this.acceptedOrder.order_id, 'p2p', url);
            this.showSelectYourBank = false;
            this.loading = false;
            this.show = false;
        }

        /**
         * Load previous transfer data for this third party order.
         * If prev transfer exists - redirect to login page,
         * if not - to registation page
         */
        async function loadPrevTransfer() {
            try {
                const { company_slug: company } = this.selectedOrder;
                const { kaoshi_email: kaoshiEmail } = this.$store.state.user;

                const { data: userCompanies } = await Vue.backendApi().emailAPI({
                    url: `/console/api/v1/brands/email?email=${kaoshiEmail}`,
                    method: 'get',
                });

                this.isPrevTransfer = userCompanies.find(({ code }) => code === company);
            } catch (error) {
                this.$swal({
                    type: 'error',
                    title: 'Error loading transfers',
                    text: error,
                });
            }
        }

        function redirectToTransfers() {
            if (!this.isFromTransfers) {
                setTimeout(() => {
                    this.$router.push({
                        name: 'transfers',
                    });
                }, 1000);
            }

            this.show = false;
        }

        /**
         * Return to signup or login page depending on prev transfer data
         * @param {Object} company
         */
        // eslint-disable-next-line no-inner-declarations

        async function loadCountries() {
            try {
                const { data: countries } = await Vue.backendApi()
                    .countries()
                    .list();
                this.kaoshiCountries = countries;
            } catch (error) {
                this.$swal({
                    type: 'error',
                    title: 'Error loading countries',
                    text: error,
                });
            }
        }
    }


    function getComputed() {
        return {
            ...mapGetters('paymentMethods', [
                'paymentMethods',
            ]),
            ...mapGetters('deliveryMethods', {
                storeDeliveryMethods: 'deliveryMethods',
            }),
            show: {
                get() {
                    return this.value
                },
                set(value) {
                    this.$emit('input', value)
                }
            },
            userCountry() {
                const countryAbbrev = this.$_user.country_abbreviation;
                return this.kaoshiCountries.find((country) => country.abbreviation === countryAbbrev) || {};
            },
            skipPaymentMethodCheck() {
                const { type } = this.selectedOrder;
                return this.userCountry
                    && this.userCountry.skip_payment_method_check
                    && (type === 'hybrid' || type === '3po');
            },
            transferModalTitle() {
                if(this.showPaymentRecipient && this.showReceipientFields) {
                    return 'Add New Recipient';
                }
                return `${ this.isPoster ? 'Receive' : 'Send' } money`;
            },
            currentStep() {
                const secondStep = !this.showFeeModal && !this.showPaymentRequest && !this.showPaymentRecipient;
                const thirdStep = secondStep && !this.showConfirmTransfer && !this.showConfirmPaymentRequest;

                if (this.selectedOrder.is_partial_match_allowed) {
                    if (this.showMatcherAmountDialog) {
                        return 1
                    }

                    if(thirdStep) return 4;
                    else if(secondStep) return 3;
                    return 2
                }

                if(thirdStep) return 3;
                else if(secondStep) return 2;
                return 1
            },
            isLastStep() {
                if(this.isPoster) {
                    return this.currentStep > 1;
                }
                return this.currentStep > 2;
            },
            matcherPaymentMethod() {
                const {
                    type,
                    matcher_country: matcherCountry,
                    matcher_currency: matcherCurrency,
                    matcher_payment_provider,
                    poster_payment_method_type: posterPaymentMethodType,
                } = this.selectedOrder;

                if(type === '3po') {
                    return null;
                }

                if(type === 'hybrid' && this.selectedOrder.isNgHybrid) {
                    return null;
                }

                if(type === 'hybrid') {
                    return {
                        name: this.capitalize(matcher_payment_provider),
                        logo: this.$_getPaymentLogoPath(matcher_payment_provider),
                    }
                }

                if (ServerUtils.isDevOrLocal() && posterPaymentMethodType === 'kaoshi_test_payment') {
                    return {
                        name: 'KaoshiTest',
                        logo: this.$_getPaymentLogoPath('kaoshi_test_payment'),
                    }
                }

                if (matcherCountry === 'US') {
                    return {
                        name: 'Zelle',
                        logo: this.$_getPaymentLogoPath('zelle_payment'),
                    };
                }

                if (matcherCountry === 'GB') {
                    return {
                        name: 'Faster Payments',
                        logo: this.$_getPaymentLogoPath('faster_payment'),
                    };
                }

                if (matcherCountry === 'CA') {
                    return {
                        name: 'Interac',
                        logo: this.$_getPaymentLogoPath('interac_payment'),
                    };
                }

                if (matcherCurrency === 'EUR') {
                    return {
                        name: 'Sepa',
                        logo: this.$_getPaymentLogoPath('sepa_payment'),
                    };
                }

                return null;
            },
            showWorldremitInformation() {
                return this.matcherPaymentMethod.name.toLowerCase() === 'worldremit';
            },
            hideMatchInformationDialog: {
                get() {
                    return this.$store.state.hideMatchInformationDialog;
                },
                set(value) {
                    this.$store.dispatch('updateMatchInformationDialog', value);
                },
            },
        };
    }
</script>

<style scoped>
.v-dialog.v-dialog--active {
    background-color: transparent !important;
}

.v-tabs >>> .v-tabs-bar__content {
    background-color: rgba(168, 168, 168, 0.12);
}

.v-tabs.primary >>> .v-tabs-bar__content {
    background-color: #181235;
}

.payment-card {
    overflow: hidden;
}

.payment-card >>> .v-toolbar {
    position: sticky;
    top: 0;
    z-index: 9;
}

.payment-card >>> #scroller {
    max-height: 600px;
    /* height: 45vh; */
    box-shadow: none !important;
    overflow-y: auto;
}

#clock .progress {
    position: absolute;
    padding: 2px;
    width: 100%;
    left: 0px;
    top: 0;
}

#clock .progress::after {
    content: ' ';
    position: absolute;
    border-radius: 50%;
    padding: 4px;
    background: #333;
    top: -2px;
    right: 0;
}

.short-height {
    line-height: 5px
}

.v-tabs >>> .v-window.v-item-group.v-tabs-items {
    border-bottom-right-radius: 20px;
}

@media screen and (max-width: 767px) {
    .v-tabs >>> .v-tabs-bar {
        height: 100px;
    }

    .v-tabs >>> .v-tabs-bar__content {
        background-color: #fff;
    }

    .v-tabs >>> .v-tabs-slider-wrapper,
    .v-tabs >>> .v-slide-group:not(.v-slide-group--has-affixes)>.v-slide-group__next,
    .v-tabs >>> .v-slide-group:not(.v-slide-group--has-affixes)>.v-slide-group__prev {
        display: none !important;
    }

}

@media only screen
  and (min-device-width: 768px)
  and (max-device-width: 1366px)
  and (-webkit-min-device-pixel-ratio: 2) {
    .payment-card #scroller {
        /* height: 33vh; */
    }
}
</style>
